import React from 'react'
import moment from 'moment'

import AddIcon from '@mui/icons-material/Add'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk'
import PushPinIcon from '@mui/icons-material/PushPin'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import SwapCallsIcon from '@mui/icons-material/SwapCalls'

import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import RosterCoverageCards from './RosterCoverageCards'
import { getRosterLabel } from './globalRosterUtils'
import useIsMobile from '../../../../helpers/useIsMobile'

const RosterControlPanel = ({
  rosters,
  selectedRoster,
  setSelectedRoster,
  openRosterDialog,
  openNewShift,
  publishRoster,
  openArchiveRoster,
  applicationsInView,
  applicationCount,
  onOpenApplications,
  shiftSwapRequestCount,
  onOpenShiftSwapRequests,
  onCallNowNames,
  coverageItems,
  displayMode,
  setDisplayMode,
  filter,
  setFilter,
  // Staff rows (week view): narrow to employees who have declared an
  // available window in the visible range
  availabilityToggleVisible = false,
  showAvailableOnly = false,
  setShowAvailableOnly = () => {},
  availableStaffCount = 0,
  handlePrev,
  handleNext,
  rangeLabel,
  rangeLabelShort,
  // Year view only: set when paging has moved off the rolling window, so a
  // one-click return to "next 12 months" is offered
  showNext12Months = null,
  viewRange,
  setViewRange,
  defaultView,
  saveDefaultView,
  savingDefaultView,
}) => {
  const compact = useIsMobile('sm')
  const viewIsDefault = viewRange === defaultView

  return (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Stack gap={2}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', lg: 'center' }}
        gap={1}
      >
        <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
          <Autocomplete
            // Phones: fill the row and keep the archive button beside it
            sx={{ minWidth: { xs: 0, sm: 320 }, flex: { xs: 1, sm: 'none' } }}
            options={rosters}
            value={selectedRoster}
            onChange={(e, value) => setSelectedRoster(value)}
            getOptionLabel={getRosterLabel}
            isOptionEqualToValue={(a, b) => a?._id === b?._id}
            renderInput={(params) => (
              <TextField {...params} size="small" label={!!!rosters?.length ? 'No Rota Plans' : "Selected rota plan" }/>
            )}
            disabled={!!!rosters?.length}
          />
          <Tooltip title="Archive rota plan">
            <span>
              <IconButton
                color="error"
                disabled={!selectedRoster}
                onClick={openArchiveRoster}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Button
            startIcon={<CheckCircleIcon />}
            variant="contained"
            disabled={!selectedRoster || selectedRoster.status === 'Published'}
            onClick={publishRoster}
            sx={
              // Same pulse as the "Draft rota plan" key - a draft is
              // waiting to be published
              selectedRoster?.status === 'Draft'
                ? {
                    animation:
                      'publishRosterPulse 1.25s ease-in-out infinite',
                    '@keyframes publishRosterPulse': {
                      '0%': {
                        boxShadow: '0 0 0 0 rgba(35, 154, 164, 0.54)',
                      },
                      '70%': {
                        boxShadow: '0 0 0 9px rgba(35, 154, 164, 0)',
                      },
                      '100%': {
                        boxShadow: '0 0 0 0 rgba(35, 154, 164, 0)',
                      },
                    },
                  }
                : undefined
            }
          >
            Publish
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={openRosterDialog}
          >
            New Rota Plan
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            disabled={!selectedRoster}
            onClick={() => openNewShift(moment())}
          >
            Add Shift
          </Button>
          <Chip
            size="small"
            color={applicationCount ? 'anPurple' : 'success'}
            label={`${applicationCount} pending application${applicationCount === 1 ? '' : 's'}`}
            icon={<PersonAddIcon />}
            onClick={onOpenApplications}
            sx={{ borderRadius: 1, height: 34 }}
          />
          <Chip
            size="small"
            color={shiftSwapRequestCount ? 'anPurple' : 'success'}
            label={`${shiftSwapRequestCount} shift swap request${shiftSwapRequestCount === 1 ? '' : 's'}`}
            icon={<SwapCallsIcon />}
            onClick={onOpenShiftSwapRequests}
            sx={{ borderRadius: 1, height: 34 }}
          />
          {onCallNowNames?.length > 0 && (
            <Tooltip title="Currently on call">
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`On call now: ${onCallNowNames.join(', ')}`}
                icon={<PhoneInTalkIcon />}
                sx={{ borderRadius: 1, height: 34, borderStyle: 'dashed' }}
              />
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Divider />

      <RosterCoverageCards coverageItems={coverageItems} />

      <Divider />

      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        gap={2}
      >
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <FormControl
            size="small"
            sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: 1, sm: 'none' } }}
          >
            <InputLabel>Display</InputLabel>
            <Select
              value={displayMode}
              label="Display"
              onChange={(e) => setDisplayMode(e.target.value)}
            >
              {/* The year view has no rows to group - it colours days by
                  coverage or by source, so only those two apply there */}
              <MenuItem value="staff">
                {viewRange === 'year' ? 'Coverage' : 'Staff'}
              </MenuItem>
              {viewRange !== 'year' && <MenuItem value="role">Role</MenuItem>}
              <MenuItem value="source">Source</MenuItem>
              {viewRange !== 'year' && (
                <MenuItem value="roster">Rota Plan</MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: 1, sm: 'none' } }}
          >
            <InputLabel>Filter</InputLabel>
            <Select
              value={filter}
              label="Filter"
              onChange={(e) => setFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="events">Events Only</MenuItem>
              <MenuItem value="pts">PTS Only</MenuItem>
              <MenuItem value="roster">Rota Plan Only</MenuItem>
              <MenuItem value="oncall">On Call Only</MenuItem>
              <MenuItem value="vacant">Vacant Only</MenuItem>
              <MenuItem value="assigned">Assigned Only</MenuItem>
            </Select>
          </FormControl>
          {availabilityToggleVisible && (
            <Tooltip title="Only show staff who have said they can work during this week (from My Availability)">
              <FormControlLabel
                // Phones: drop onto its own line rather than squeezing the
                // Display / Filter selects beside it
                sx={{ ml: 0, mr: 0, flexBasis: { xs: '100%', sm: 'auto' } }}
                control={
                  <Switch
                    size="small"
                    color="success"
                    checked={showAvailableOnly}
                    onChange={(e) => setShowAvailableOnly(e.target.checked)}
                  />
                }
                label={
                  <Stack direction="row" gap={0.75} alignItems="center">
                    <Typography variant="body2" fontWeight={600} noWrap>
                      Available staff only
                    </Typography>
                    <Chip
                      size="small"
                      icon={<EventAvailableIcon />}
                      label={availableStaffCount}
                      color={availableStaffCount ? 'success' : 'default'}
                      variant={availableStaffCount ? 'filled' : 'outlined'}
                      sx={{ height: 22, borderRadius: 1 }}
                    />
                  </Stack>
                }
              />
            </Tooltip>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
        >
          <IconButton onClick={handlePrev} sx={{ flexShrink: 0 }}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            textAlign="center"
            noWrap
            sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: 1, sm: 'none' } }}
          >
            {compact ? (rangeLabelShort ?? rangeLabel) : rangeLabel}
          </Typography>
          <IconButton onClick={handleNext} sx={{ flexShrink: 0 }}>
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
          {showNext12Months && (
            <Tooltip title="Back to the next 12 months from this month">
              <Chip
                size="small"
                clickable
                variant="outlined"
                label={compact ? 'Next 12m' : 'Next 12 months'}
                onClick={showNext12Months}
                sx={{ borderRadius: 1, height: 30, flexShrink: 0 }}
              />
            </Tooltip>
          )}
          <FormControl
            size="small"
            sx={{ minWidth: { xs: 104, sm: 130 }, flexShrink: 0 }}
          >
            <InputLabel>View</InputLabel>
            <Select
              value={viewRange}
              label="View"
              onChange={(e) => setViewRange(e.target.value)}
            >
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
              <MenuItem value="year">Year</MenuItem>
            </Select>
          </FormControl>
          {viewIsDefault ? (
            <Tooltip title="This view opens by default">
              <PushPinIcon
                fontSize="small"
                sx={{ color: 'text.disabled', flexShrink: 0 }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Open the Global Roster in this view by default">
              <span>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<PushPinOutlinedIcon />}
                  disabled={savingDefaultView}
                  onClick={saveDefaultView}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {compact ? 'Default' : 'Set as default'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Stack>
  </Paper>
  )
}

export default RosterControlPanel
